import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useCreateMyApplePayRegistration } from '@/lib/api/apple-pay-registrations';

/**
 * Merchant setup wizard (create step). Collects the Apple merchant identifier,
 * display name, and domain-association file, then creates the BYOK registration.
 * Certificates and domains are added afterwards from the management view.
 */
export function SetupWizard({ trigger }: { trigger: React.ReactNode }) {
  const create = useCreateMyApplePayRegistration();
  const [open, setOpen] = React.useState(false);
  const [merchantIdentifier, setMerchantIdentifier] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [associationFileContent, setAssociationFileContent] = React.useState('');

  const canSubmit =
    /^merchant\./.test(merchantIdentifier.trim()) &&
    !!displayName.trim() &&
    displayName.trim().length <= 64 &&
    !create.isPending;

  const reset = () => {
    setMerchantIdentifier('');
    setDisplayName('');
    setAssociationFileContent('');
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setAssociationFileContent(text.trim());
  };

  const handleSubmit = () => {
    create.mutate(
      {
        merchantIdentifier: merchantIdentifier.trim(),
        displayName: displayName.trim(),
        associationFileContent: associationFileContent.trim() || undefined,
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bring your own Apple Pay certificates</DialogTitle>
          <DialogDescription>
            Show your own business name in the Apple Pay sheet by using your own Apple merchant ID and
            certificates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/40 rounded-md border p-3">
            <p className="text-sm font-medium">Before you start (in the Apple Developer portal)</p>
            <ol className="text-muted-foreground mt-2 list-decimal space-y-1 pl-4 text-xs">
              <li>Create an Apple merchant identifier (e.g. merchant.com.acme.shop).</li>
              <li>
                Generate two certificates: a <strong>payment processing</strong> cert (must be{' '}
                <strong>EC P-256</strong>) and a <strong>merchant identity</strong> cert. Export each as a
                password-protected <code>.p12</code>.
              </li>
              <li>Download your domain-association file — you&apos;ll paste it below.</li>
            </ol>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Apple merchant identifier</Label>
            <Input
              placeholder="merchant.com.acme.shop"
              value={merchantIdentifier}
              onChange={(e) => setMerchantIdentifier(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Display name (a label to identify this certificate, ≤ 64 chars)</Label>
            <Input
              placeholder="ACME Store"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={64}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Apple domain-association file</Label>
            <Input type="file" onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
            <Textarea
              placeholder="…or paste the association file contents"
              value={associationFileContent}
              onChange={(e) => setAssociationFileContent(e.target.value)}
              className="h-20 font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              reset();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Creating…' : 'Create & continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
